import * as Ariakit from '@ariakit/react'
import type { QuickOrderManualRow } from '@haus-storefront-react/quick-order'
import { useQuickOrderSkuAutocomplete } from '@haus-storefront-react/quick-order'
import { debounce } from 'lodash-es'
import { type ChangeEvent, type FocusEvent, useEffect, useMemo, useState } from 'react'
import { Typography } from '@components'
import styles from './QuickOrder.module.scss'
import inputStyles from '@components/input/Input.module.scss'

export interface QuickOrderSkuSuggestion {
  label: string
  sku: string
  productVariantId: string
  productVariantName?: string
  productName?: string
  inStock: boolean
}

interface ManualSkuFieldProps {
  row: QuickOrderManualRow
  inputAriaLabel: string
  hasError: boolean
  searchingLabel: string
  noResultsLabel: string
  onInputChange: (rowId: string, value: string) => void
}

const getSuggestionDisplayName = (suggestion: QuickOrderSkuSuggestion): string => {
  const primaryName = suggestion.productVariantName || suggestion.productName
  if (primaryName && primaryName !== 'undefined') {
    return primaryName
  }

  const fromLabel = suggestion.label.replace(`${suggestion.sku} - `, '').trim()
  if (fromLabel && fromLabel !== 'undefined') {
    return fromLabel
  }

  return suggestion.sku
}

const findSuggestionBySku = (
  list: QuickOrderSkuSuggestion[],
  rawValue: string,
): QuickOrderSkuSuggestion | null => {
  const normalizedValue = rawValue.trim().toLowerCase()
  if (!normalizedValue) {
    return null
  }

  const match = list.find(
    (suggestion: QuickOrderSkuSuggestion) => suggestion.sku.toLowerCase() === normalizedValue,
  )
  if (!match) {
    return null
  }

  return match
}

/**
 * Render a single manual Quick Order SKU combobox row.
 */
export const ManualSkuField = ({
  row,
  inputAriaLabel,
  hasError,
  searchingLabel,
  noResultsLabel,
  onInputChange,
}: ManualSkuFieldProps) => {
  const [debouncedTerm, setDebouncedTerm] = useState<string>(row.identifier.trim())
  const [displayedSuggestions, setDisplayedSuggestions] = useState<QuickOrderSkuSuggestion[]>([])
  const updateDebouncedTerm = useMemo(
    () =>
      debounce((nextValue: string) => {
        setDebouncedTerm(nextValue)
      }, 250),
    [],
  )
  useEffect(() => {
    updateDebouncedTerm(row.identifier.trim())

    return () => {
      updateDebouncedTerm.cancel()
    }
  }, [row.identifier, updateDebouncedTerm])

  const [pendingCommitLookupValue, setPendingCommitLookupValue] = useState<string | null>(null)
  const combobox = Ariakit.useComboboxStore({
    value: row.identifier,
    setValue: (value) => {
      onInputChange(row.id, value)
    },
  })

  const query = useQuickOrderSkuAutocomplete(debouncedTerm, 8)
  const suggestions = useMemo(() => query.data?.items ?? [], [query.data?.items])

  useEffect(() => {
    if (query.isFetching) {
      return
    }

    setDisplayedSuggestions(suggestions)
  }, [query.isFetching, suggestions])

  useEffect(() => {
    if (!pendingCommitLookupValue) {
      return
    }

    const pendingLookupTerm = pendingCommitLookupValue.trim()
    if (!pendingLookupTerm) {
      combobox.hide()
      setPendingCommitLookupValue(null)
      return
    }

    if (debouncedTerm !== pendingLookupTerm) {
      updateDebouncedTerm.cancel()
      setDebouncedTerm(pendingLookupTerm)
      return
    }

    if (query.isFetching) {
      return
    }

    const lookupSuggestions = query.data?.items ?? []
    const match = findSuggestionBySku(lookupSuggestions, pendingCommitLookupValue)

    if (match) {
      combobox.hide()
      setPendingCommitLookupValue(null)
      return
    }

    combobox.hide()
    setPendingCommitLookupValue(null)
  }, [
    combobox,
    debouncedTerm,
    pendingCommitLookupValue,
    query.data?.items,
    query.isFetching,
    row.id,
    updateDebouncedTerm,
  ])

  const trimmedInputValue = row.identifier.trim()
  const hasTypedValue = trimmedInputValue.length > 0
  const isSearchTermSynced = debouncedTerm === trimmedInputValue
  const shouldShowSearching = query.isFetching && displayedSuggestions.length === 0
  const noResults =
    !query.isFetching && hasTypedValue && isSearchTermSynced && suggestions.length === 0
  const hasPopoverContent = shouldShowSearching || noResults || displayedSuggestions.length > 0

  return (
    <div className={styles.autocompleteRoot}>
      <Ariakit.ComboboxProvider store={combobox}>
        <Ariakit.Combobox
          store={combobox}
          className={`${inputStyles.input} ${styles.autocompleteInput} ${hasError ? styles.autocompleteInputError : ''}`}
          aria-label={inputAriaLabel}
          aria-invalid={hasError}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            if (event.currentTarget.value.trim().length > 0) {
              combobox.show()
              return
            }
            combobox.hide()
          }}
          onBlur={(event: FocusEvent<HTMLInputElement>) => {
            const hasExactMatch = Boolean(
              findSuggestionBySku(suggestions, event.currentTarget.value),
            )
            if (hasExactMatch) {
              combobox.hide()
              return
            }

            const inputValue = event.currentTarget.value
            const trimmedInputValue = inputValue.trim()
            const shouldRunBlurLookup =
              trimmedInputValue.length > 0 &&
              (trimmedInputValue !== debouncedTerm || query.isFetching)

            if (shouldRunBlurLookup) {
              setPendingCommitLookupValue(inputValue)
              return
            }
            combobox.hide()
          }}
        />

        {hasPopoverContent && (
          <Ariakit.ComboboxPopover
            store={combobox}
            gutter={4}
            className={styles.autocompletePopover}
            sameWidth
          >
            <Ariakit.ComboboxList store={combobox} className={styles.autocompleteListbox}>
              {shouldShowSearching && (
                <div className={styles.autocompleteStatus} role="status" aria-live="polite">
                  <Typography variant="body" as="span">
                    {searchingLabel}
                  </Typography>
                </div>
              )}

              {noResults && (
                <div className={styles.autocompleteStatus}>
                  <Typography variant="body" as="span">
                    {noResultsLabel}
                  </Typography>
                </div>
              )}

              {displayedSuggestions.map((suggestion: QuickOrderSkuSuggestion) => {
                const isSelected = row.identifier.toLowerCase() === suggestion.sku.toLowerCase()
                return (
                  <Ariakit.ComboboxItem
                    store={combobox}
                    value={suggestion.sku}
                    key={suggestion.productVariantId}
                    aria-selected={isSelected}
                    className={styles.autocompleteOption}
                    onClick={() => {
                      combobox.hide()
                    }}
                  >
                    <div className={styles.autocompleteOptionColumns}>
                      <Typography variant="body" as="span">
                        {suggestion.sku}
                      </Typography>
                      <Typography
                        variant="body"
                        as="span"
                        className={styles.autocompleteOptionLabel}
                      >
                        {getSuggestionDisplayName(suggestion)}
                      </Typography>
                    </div>
                  </Ariakit.ComboboxItem>
                )
              })}
            </Ariakit.ComboboxList>
          </Ariakit.ComboboxPopover>
        )}
      </Ariakit.ComboboxProvider>
    </div>
  )
}
